import React, { useState, useEffect, useMemo } from "react";
import { Button } from "./ui/button";
import { UserPlus, Users, Minus, Maximize2, Play, Image, UserCheck } from "lucide-react";
import { apiFetch } from "../lib/api";
import { useDemoMode } from "../contexts/DemoModeContext";  // match the relative path

/* -------------------- Types -------------------- */
export type UnknownPerson = {
  pending_id: string;
  photo_url: string | null;
  clip_url: string | null;
  camera_id: string;
  is_inside: boolean;
  timestamp: number;
  declined_resident_id?: number;
  suggested_resident_id?: number | null;
  suggested_score?: number | null;
  source_type?: "crossing" | "census";
};

type Person = {
  id: number;
  name: string;
  room_number: string;
  role: string;
  photo_url: string | null;
};

type IdentifyPersonPopupProps = {
  queue: UnknownPerson[];
  people: Person[];
  onIdentifyExisting: (pendingId: string, personId: number) => Promise<void>;
  onSkipToBottom: (pendingId: string) => void;  // ADD
  onRegisterNew: (
    pendingId: string,
    data: { name: string; visiting_with_id: number | null; access_risk: boolean }
  ) => Promise<void>;
  onRegisterStaff: (
    pendingId: string,
    data: { name: string }
  ) => Promise<void>;
  onSkip: (pendingId: string) => Promise<void>;
  onClose: () => void;
};

type Mode = "select" | "register" | "register_staff";
type MediaMode = "video" | "photo";

/* -------------------- Component -------------------- */
export default function IdentifyPersonPopup({
  queue,
  people,
  onIdentifyExisting,
  onRegisterNew,
  onRegisterStaff,
  onSkipToBottom,
  onSkip,
  onClose,
}: IdentifyPersonPopupProps) {
  const [mode, setMode] = useState<Mode>("select");
  const [mediaMode, setMediaMode] = useState<MediaMode>("video");
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    visiting_with_id: null as number | null,
    access_risk: false,
  });
  const [saving, setSaving] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const { displayName } = useDemoMode();

  // Also Seen state
  const [showAlsoSeen, setShowAlsoSeen] = useState(false);
  const [alsoSeenIds, setAlsoSeenIds] = useState<number[]>([]);
  const [savingAlsoSeen, setSavingAlsoSeen] = useState(false);

  const current = queue.length > 0 ? queue[0] : null;
  const [personSearch, setPersonSearch] = useState("");
  const [visitingWithSearch, setVisitingWithSearch] = useState("");
  const [alsoSeenSearch, setAlsoSeenSearch] = useState("");

  // Reset state when current person changes
  useEffect(() => {
    setMode("select");
    setSelectedPersonId(null);
    setFormData({ name: "", visiting_with_id: null, access_risk: false });
    setMediaMode(current?.clip_url ? "video" : "photo");
    setShowAlsoSeen(false);
    setAlsoSeenIds([]);
    setPersonSearch("");
    setVisitingWithSearch(""); // ADD
    setAlsoSeenSearch(""); // ADD
  }, [current?.pending_id, current?.clip_url]);

  // Group people by role
  const groupedPeople = useMemo(() => {
    const residents = people.filter((p) => p.role === "resident");
    const staff = people.filter((p) => p.role === "staff");
    const visitors = people.filter((p) => p.role === "visitor");
    return { residents, staff, visitors };
  }, [people]);

  // Residents only for "visiting with" dropdown
  const residentsForVisitingWith = useMemo(() => {
    return people.filter((p) => p.role === "resident");
  }, [people]);

  const filteredPeople = useMemo(() => {
    const q = personSearch.trim().toLowerCase();
    if (!q) return people;
    return people.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.room_number || "").toLowerCase().includes(q)
    );
  }, [people, personSearch]);

  // NEW: the person the backend suggested for this crossing, looked
  // up from the FULL `people` list (not filteredPeople) so the
  // suggestion stays pinned at the top even while the reviewer is
  // actively typing an unrelated search — it represents the system's
  // claim about identity, independent of what's currently being
  // searched for.
  const suggestedPerson = useMemo(() => {
    const suggestedId = current?.suggested_resident_id;
    if (suggestedId == null) return null;
    return people.find(p => p.id === suggestedId) ?? null;
  }, [people, current?.suggested_resident_id]);

  // CHANGED: each role group now excludes the suggested person, since
  // they're rendered once in their own pinned section above all three
  // groups instead.
  const filteredGrouped = useMemo(() => {
    const suggestedId = current?.suggested_resident_id;
    const excludeSuggested = (list: Person[]) =>
      suggestedId == null ? list : list.filter(p => p.id !== suggestedId);

    return {
      residents: excludeSuggested(filteredPeople.filter(p => p.role === "resident")),
      staff: excludeSuggested(filteredPeople.filter(p => p.role === "staff")),
      visitors: excludeSuggested(filteredPeople.filter(p => p.role === "visitor")),
    };
  }, [filteredPeople, current?.suggested_resident_id]);


  const filteredResidentsForVisitingWith = useMemo(() => {
    const q = visitingWithSearch.trim().toLowerCase();
    if (!q) return residentsForVisitingWith;
    return residentsForVisitingWith.filter(r =>
        r.name.toLowerCase().includes(q) ||
        (r.room_number || "").toLowerCase().includes(q)
    );
  }, [residentsForVisitingWith, visitingWithSearch]);

  const filteredPeopleForAlsoSeen = useMemo(() => {
    const q = alsoSeenSearch.trim().toLowerCase();
    if (!q) return people;
    return people.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.room_number || "").toLowerCase().includes(q)
    );
  }, [people, alsoSeenSearch]);

  const formatDateTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const time = new Intl.DateTimeFormat("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
        timeZone: "America/New_York",
    }).format(date);
    const day = new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "America/New_York",
    }).format(date);
    return { time, day };
  };

  const handleIdentifyExisting = async () => {
    if (!current || !selectedPersonId) return;

    setSaving(true);
    try {
      await onIdentifyExisting(current.pending_id, selectedPersonId);
      if (current.clip_url) {
        setShowAlsoSeen(true);
      }
    } catch (err) {
      console.error("Failed to identify person:", err);
      alert("Failed to identify. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleRegisterNew = async () => {
    if (!current || !formData.name.trim()) return;

    setSaving(true);
    try {
      await onRegisterNew(current.pending_id, {
        name: formData.name.trim(),
        visiting_with_id: formData.visiting_with_id,
        access_risk: formData.access_risk,
      });
      if (current.clip_url) {
        setShowAlsoSeen(true);
      }
    } catch (err) {
      console.error("Failed to register visitor:", err);
      alert("Failed to register. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleRegisterStaff = async () => {
    if (!current || !formData.name.trim()) return;

    setSaving(true);
    try {
      await onRegisterStaff(current.pending_id, {
        name: formData.name.trim(),
      });
      if (current.clip_url) {
        setShowAlsoSeen(true);
      }
    } catch (err) {
      console.error("Failed to register staff:", err);
      alert("Failed to register. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!current) return;

    setSaving(true);
    try {
        await onSkip(current.pending_id);
    } catch (err) {
        console.error("Failed to delete:", err);
        alert("Failed to delete. Please try again.");
    } finally {
        setSaving(false);
    }
  };

const handleSkipToBottom = () => {
    if (!current) return;
    onSkipToBottom(current.pending_id);
  };

  const handleAlsoSeenToggle = (personId: number) => {
    setAlsoSeenIds((prev) =>
      prev.includes(personId)
        ? prev.filter((id) => id !== personId)
        : [...prev, personId]
    );
  };

  const handleAlsoSeenSubmit = async () => {
    if (!current || alsoSeenIds.length === 0) return;

    setSavingAlsoSeen(true);
    try {
      await apiFetch("/api/also-seen", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          resident_ids: alsoSeenIds,
          is_inside: current.is_inside,
          timestamp: current.timestamp,
          clip_url: current.clip_url,
          camera_id: current.camera_id,
        }),
      });
      setAlsoSeenIds([]);
      setShowAlsoSeen(false);
    } catch (err) {
      console.error("Failed to log also seen:", err);
      alert("Failed to log others. Please try again.");
    } finally {
      setSavingAlsoSeen(false);
    }
  };

  if (!current) return null;

  const hasVideo = !!current.clip_url;
  const hasPhoto = !!current.photo_url;

  // === MINIMIZED VIEW ===
  if (minimized) {
    return (
      <div className="fixed bottom-4 right-4 z-[100]">
        <button
          onClick={() => setMinimized(false)}
          className="flex items-center gap-3 px-4 py-3 bg-amber-600 hover:bg-amber-500 text-white rounded-xl shadow-lg transition-all"
        >
          <UserPlus className="w-5 h-5" />
          <span className="font-medium">
            {queue.length} Unknown {queue.length === 1 ? "Person" : "People"}
          </span>
          <Maximize2 className="w-4 h-4 ml-2" />
        </button>
      </div>
    );
  }

  // === ALSO SEEN VIEW ===
  if (showAlsoSeen) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/70" onClick={() => setShowAlsoSeen(false)} />

        <div className="relative w-[92vw] max-w-md rounded-2xl bg-[#0d1117] border border-teal-500/50 shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center gap-2 mb-4">
            <UserCheck className="w-6 h-6 text-teal-400" />
            <h3 className="text-xl font-bold text-white">Anyone Else in This Clip?</h3>
          </div>

          <hr className="border-white/10 mb-4" />

          {hasVideo && (
            <div className="flex justify-center mb-4">
              <video
                src={`https://api.monivue.io${current.clip_url}`}
                autoPlay
                loop
                muted
                playsInline
                className="w-72 h-80 object-contain rounded-lg border border-white/20"
              />
            </div>
          )}

          <p className="text-sm text-gray-400 mb-4 text-center">
            Select anyone else who {current.is_inside ? "entered" : "exited"} in this clip
          </p>

          {/* Search */}
          <input
              type="text"
              value={alsoSeenSearch}
              onChange={(e) => setAlsoSeenSearch(e.target.value)}
              placeholder="Search by name or room..."
              className="w-full px-3 py-2 mb-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-teal-500 text-sm"
          />

          <div className="space-y-2 max-h-[40vh] overflow-y-auto mb-4">
              {filteredPeopleForAlsoSeen.length === 0 ? (
                  <div className="px-3 py-4 text-center text-gray-400 text-sm">
                      No results found
                  </div>
              ) : (
                  filteredPeopleForAlsoSeen.map((p) => (
                      <label
                          key={p.id}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                              alsoSeenIds.includes(p.id)
                                  ? "bg-teal-600/20 border border-teal-500/50"
                                  : "bg-gray-800 hover:bg-gray-700"
                          }`}
                      >
                          <input
                              type="checkbox"
                              checked={alsoSeenIds.includes(p.id)}
                              onChange={() => handleAlsoSeenToggle(p.id)}
                              className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-teal-500 focus:ring-teal-500"
                          />
                          {p.photo_url && (
                              <img
                                  src={"https://api.monivue.io" + p.photo_url}
                                  className="w-7 h-7 rounded-full object-cover bg-gray-600 flex-shrink-0"
                              />
                          )}
                        <span className="text-white">{displayName(p.id, p.name)}</span>
                        <span className="text-gray-400 text-sm">
                              {p.role === "resident" && p.room_number
                                  ? `(Room ${p.room_number})`
                                  : `(${p.role})`}
                          </span>
                      </label>
                  ))
              )}
          </div>

          <div className="flex gap-3">
            <Button
              className="flex-1 bg-gray-700 hover:bg-gray-600"
              onClick={() => setShowAlsoSeen(false)}
            >
              Skip
            </Button>
            <Button
              className="flex-1 bg-teal-600 hover:bg-teal-500 font-semibold"
              onClick={handleAlsoSeenSubmit}
              disabled={savingAlsoSeen || alsoSeenIds.length === 0}
            >
              {savingAlsoSeen
                ? "Saving..."
                : `Log ${alsoSeenIds.length} ${current.is_inside ? "Entry" : "Exit"}${alsoSeenIds.length !== 1 ? "s" : ""}`}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // === FULL VIEW ===
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70" onClick={() => setMinimized(true)} />

      <div className="relative w-[92vw] max-w-md rounded-2xl bg-[#0d1117] border border-amber-500/50 shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <UserPlus className="w-6 h-6 text-amber-400" />
            <h3 className="text-xl font-bold text-white">Identify Person</h3>
          </div>
          <div className="flex items-center gap-2">
            {queue.length > 1 && (
              <span className="text-sm text-gray-400">
                {queue.length} in queue
              </span>
            )}
            <button
              onClick={() => setMinimized(true)}
              className="p-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
              title="Minimize"
            >
              <Minus className="w-4 h-4" />
            </button>
          </div>
        </div>

        <hr className="border-white/10 mb-4" />

        {/* Media Toggle */}
        {hasVideo && hasPhoto && (
          <div className="flex justify-center gap-2 mb-3">
            <button
              onClick={() => setMediaMode("video")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                mediaMode === "video"
                  ? "bg-amber-600 text-white"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
            >
              <Play className="w-3.5 h-3.5" />
              Video
            </button>
            <button
              onClick={() => setMediaMode("photo")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                mediaMode === "photo"
                  ? "bg-amber-600 text-white"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
            >
              <Image className="w-3.5 h-3.5" />
              Photo
            </button>
          </div>
        )}

        {/* Media Display */}
        <div className="flex justify-center mb-4">
          {mediaMode === "video" && hasVideo ? (
            <video
              src={`https://api.monivue.io${current.clip_url}`}
              autoPlay
              loop
              muted
              playsInline
              className="w-48 h-60 object-cover rounded-lg border border-white/20"
            />
          ) : hasPhoto ? (
            <img
              src={`https://api.monivue.io${current.photo_url}`}
              alt="Unknown person"
              className="w-32 h-40 object-cover rounded-lg border border-white/20"
            />
          ) : (
            <div className="w-32 h-40 bg-gray-700 rounded-lg flex items-center justify-center">
              <span className="text-gray-400 text-sm">No media</span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="text-center text-sm text-gray-400 mb-4">
            {current.source_type === "census" && (
                <span className="inline-block px-2 py-0.5 mb-1 rounded-full text-xs bg-blue-900/40 text-blue-300 border border-blue-500/30">
                    Census sighting
                </span>
            )}
            <div>
                Detected at {formatDateTime(current.timestamp).time} • {formatDateTime(current.timestamp).day} • Camera: {current.camera_id}
            </div>
        </div>

        {/* Mode toggle — three options */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <button
            onClick={() => setMode("select")}
            className={`py-2 px-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${
              mode === "select"
                ? "bg-blue-600 text-white"
                : "bg-gray-800 text-gray-300 hover:bg-gray-700"
            }`}
          >
            <Users className="w-4 h-4" />
            Existing
          </button>
          <button
            onClick={() => setMode("register")}
            className={`py-2 px-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${
              mode === "register"
                ? "bg-amber-600 text-white"
                : "bg-gray-800 text-gray-300 hover:bg-gray-700"
            }`}
          >
            <UserPlus className="w-4 h-4" />
            Visitor
          </button>
          <button
            onClick={() => setMode("register_staff")}
            className={`py-2 px-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${
              mode === "register_staff"
                ? "bg-emerald-600 text-white"
                : "bg-gray-800 text-gray-300 hover:bg-gray-700"
            }`}
          >
            <UserCheck className="w-4 h-4" />
            Staff
          </button>
        </div>

        {/* Mode: Select existing person */}
        {mode === "select" && (
          <div className="space-y-4">
              <div>
                  <label className="block text-sm text-gray-300 mb-1">
                      Select Person
                  </label>

                  {/* Search input */}
                  <input
                      type="text"
                      value={personSearch}
                      onChange={(e) => setPersonSearch(e.target.value)}
                      placeholder="Search by name or room..."
                      className="w-full px-3 py-2 mb-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 text-sm"
                      autoFocus
                  />

                  {/* Scrollable results list */}
                  <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-600 bg-gray-800">
                      {filteredPeople.length === 0 && !suggestedPerson ? (
                          <div className="px-3 py-4 text-center text-gray-400 text-sm">
                              No results found
                          </div>
                      ) : (
                          <>
                              {/* NEW: pinned suggested-match section, rendered
                                  once above all role groups regardless of
                                  the suggested person's actual role. */}
                              {suggestedPerson && (
                                  <div>
                                      <div className="px-3 py-1.5 text-xs font-semibold text-amber-400 uppercase tracking-wide bg-amber-900/20 sticky top-0">
                                          Suggested Match
                                      </div>
                                      <button
                                          onClick={() => setSelectedPersonId(suggestedPerson.id)}
                                          className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                                              selectedPersonId === suggestedPerson.id
                                                  ? "bg-blue-600/30 border-l-2 border-blue-500"
                                                  : "bg-amber-600/10 border-l-2 border-amber-400 hover:bg-amber-600/20"
                                          }`}
                                      >
                                          {suggestedPerson.photo_url && (
                                              <img
                                                  src={`https://api.monivue.io${suggestedPerson.photo_url}`}
                                                  className="w-8 h-8 rounded-full object-cover bg-gray-600 flex-shrink-0"
                                              />
                                          )}
                                          <div className="flex-1">
                                              <p className="text-white text-sm font-medium">
                                                  {suggestedPerson.name}
                                                  {current.suggested_score != null && (
                                                      <span className="ml-2 text-xs text-amber-400">
                                                          {Math.round(current.suggested_score * 100)}%
                                                      </span>
                                                  )}
                                              </p>
                                              <p className="text-gray-400 text-xs">
                                                  {suggestedPerson.role === "resident" && suggestedPerson.room_number
                                                      ? `Room ${suggestedPerson.room_number}`
                                                      : suggestedPerson.role}
                                              </p>
                                          </div>
                                      </button>
                                  </div>
                              )}

                              {filteredGrouped.residents.length > 0 && (
                                  <div>
                                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-900/50 sticky top-0">
                                          Residents
                                      </div>
                                      {filteredGrouped.residents.map(p => (
                                          <button
                                              key={p.id}
                                              onClick={() => setSelectedPersonId(p.id)}
                                              className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                                                  selectedPersonId === p.id
                                                      ? "bg-blue-600/30 border-l-2 border-blue-500"
                                                      : "hover:bg-gray-700"
                                              }`}
                                          >
                                              {p.photo_url && (
                                                  <img
                                                      src={`https://api.monivue.io${p.photo_url}`}
                                                      className="w-8 h-8 rounded-full object-cover bg-gray-600 flex-shrink-0"
                                                  />
                                              )}
                                              <div>
                                                  <p className="text-white text-sm font-medium">{displayName(p.id,p.name)}</p>
                                                  {p.room_number && (
                                                      <p className="text-gray-400 text-xs">Room {p.room_number}</p>
                                                  )}
                                              </div>
                                          </button>
                                      ))}
                                  </div>
                              )}

                              {filteredGrouped.staff.length > 0 && (
                                  <div>
                                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-900/50 sticky top-0">
                                          Staff
                                      </div>
                                      {filteredGrouped.staff.map(p => (
                                          <button
                                              key={p.id}
                                              onClick={() => setSelectedPersonId(p.id)}
                                              className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                                                  selectedPersonId === p.id
                                                      ? "bg-blue-600/30 border-l-2 border-blue-500"
                                                      : "hover:bg-gray-700"
                                              }`}
                                          >
                                              {p.photo_url && (
                                                  <img
                                                      src={`https://api.monivue.io${p.photo_url}`}
                                                      className="w-8 h-8 rounded-full object-cover bg-gray-600 flex-shrink-0"
                                                  />
                                              )}
                                              <div>
                                                  <p className="text-white text-sm font-medium">{displayName(p.id,p.name)}</p>
                                                  <p className="text-gray-400 text-xs">Staff</p>
                                              </div>
                                          </button>
                                      ))}
                                  </div>
                              )}

                              {filteredGrouped.visitors.length > 0 && (
                                  <div>
                                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-900/50 sticky top-0">
                                          Visitors
                                      </div>
                                      {filteredGrouped.visitors.map(p => (
                                          <button
                                              key={p.id}
                                              onClick={() => setSelectedPersonId(p.id)}
                                              className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                                                  selectedPersonId === p.id
                                                      ? "bg-blue-600/30 border-l-2 border-blue-500"
                                                      : "hover:bg-gray-700"
                                              }`}
                                          >
                                              {p.photo_url && (
                                                  <img
                                                      src={`https://api.monivue.io${p.photo_url}`}
                                                      className="w-8 h-8 rounded-full object-cover bg-gray-600 flex-shrink-0"
                                                  />
                                              )}
                                              <div>
                                                  <p className="text-white text-sm font-medium">{displayName(p.id,p.name)}</p>
                                                  <p className="text-gray-400 text-xs">Visitor</p>
                                              </div>
                                          </button>
                                      ))}
                                  </div>
                              )}
                          </>
                      )}
                  </div>

                  {/* Selected person confirmation */}
                  {selectedPersonId && (
                      <p className="mt-2 text-xs text-blue-400">
                          Selected: {people.find(p => p.id === selectedPersonId)?.name}
                      </p>
                  )}
              </div>

              <div className="flex gap-3 mt-2">
                <Button
                    className="flex-1 bg-gray-700 hover:bg-gray-600"
                    onClick={handleSkipToBottom}
                    disabled={saving}
                >
                  Skip
                </Button>
                <Button
                    className="flex-1 bg-red-900 hover:bg-red-800"
                    onClick={handleDelete}
                    disabled={saving}
                >
                  {saving ? "Deleting..." : "Delete"}
                </Button>
                <Button
                    className="flex-1 bg-blue-600 hover:bg-blue-500 font-semibold"
                    onClick={handleIdentifyExisting}
                    disabled={saving || !selectedPersonId}
                >
                  {saving ? "Saving..." : "Confirm Identity"}
                </Button>
              </div>
          </div>
        )}
        {/* Mode: Register new visitor */}
        {mode === "register" && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Name *</label>
                <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                        setFormData((prev) => ({...prev, name: e.target.value}))
                    }
                    placeholder="Enter visitor name"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">
                  Visiting With
              </label>

              {/* Search input */}
              <input
                  type="text"
                  value={visitingWithSearch}
                  onChange={(e) => setVisitingWithSearch(e.target.value)}
                  placeholder="Search resident by name or room..."
                  className="w-full px-3 py-2 mb-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-amber-500 text-sm"
              />

              {/* Scrollable results */}
              <div className="max-h-36 overflow-y-auto rounded-lg border border-gray-600 bg-gray-800">
                  {/* Clear selection option */}
                  <button
                      onClick={() => {
                          setFormData(prev => ({ ...prev, visiting_with_id: null }));
                          setVisitingWithSearch("");
                      }}
                      className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                          formData.visiting_with_id === null
                              ? "bg-amber-600/30 border-l-2 border-amber-500 text-amber-300"
                              : "text-gray-400 hover:bg-gray-700"
                      }`}
                  >
                      No selection
                  </button>

                  {filteredResidentsForVisitingWith.length === 0 ? (
                      <div className="px-3 py-3 text-center text-gray-400 text-sm">
                          No residents found
                      </div>
                  ) : (
                      filteredResidentsForVisitingWith.map(r => (
                          <button
                              key={r.id}
                              onClick={() => {
                                  setFormData(prev => ({ ...prev, visiting_with_id: r.id }));
                                  setVisitingWithSearch("");
                              }}
                              className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                                  formData.visiting_with_id === r.id
                                      ? "bg-amber-600/30 border-l-2 border-amber-500"
                                      : "hover:bg-gray-700"
                              }`}
                          >
                              {r.photo_url && (
                                  <img
                                      src={"https://api.monivue.io" + r.photo_url}
                                      className="w-7 h-7 rounded-full object-cover bg-gray-600 flex-shrink-0"
                                  />
                              )}
                              <div>
                                  <p className="text-white text-sm font-medium">{displayName(r.id,r.name)}</p>
                                  {r.room_number && (
                                      <p className="text-gray-400 text-xs">Room {r.room_number}</p>
                                  )}
                              </div>
                          </button>
                      ))
                  )}
              </div>

              {/* Selected confirmation */}
              {formData.visiting_with_id && (
                  <p className="mt-1.5 text-xs text-amber-400">
                      Visiting: {displayName(
                                    residentsForVisitingWith.find(r => r.id === formData.visiting_with_id)?.id,
                                    residentsForVisitingWith.find(r => r.id === formData.visiting_with_id)?.name
                                )}
                  </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="access-risk-identify"
                checked={formData.access_risk}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, access_risk: e.target.checked }))
                }
                className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-amber-500 focus:ring-amber-500"
              />
              <label htmlFor="access-risk-identify" className="text-sm text-gray-300">
                Access Risk
              </label>
            </div>

            <div className="flex gap-3 mt-2">
              <Button
                  className="flex-1 bg-gray-700 hover:bg-gray-600"
                  onClick={handleSkipToBottom}
                  disabled={saving}
              >
                Skip
              </Button>
              <Button
                  className="flex-1 bg-red-900 hover:bg-red-800"
                  onClick={handleDelete}
                  disabled={saving}
              >
                {saving ? "Deleting..." : "Delete"}
              </Button>
              <Button
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                onClick={handleRegisterNew}
                disabled={saving || !formData.name.trim()}
            >
                {saving ? "Saving..." : "Register Visitor"}
            </Button>
            </div>
            </div>
        )}

        {/* Mode: Register new staff */}
        {mode === "register_staff" && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Name *</label>
                <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                        setFormData((prev) => ({...prev, name: e.target.value}))
                    }
                    placeholder="Enter staff name"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                autoFocus
              />
            </div>

            <p className="text-xs text-gray-400">
              Staff will be enrolled using the embeddings captured from this clip.
            </p>

            <div className="flex gap-3 mt-2">
              <Button
                  className="flex-1 bg-gray-700 hover:bg-gray-600"
                  onClick={handleSkipToBottom}
                  disabled={saving}
              >
                Skip
              </Button>
              <Button
                  className="flex-1 bg-red-900 hover:bg-red-800"
                  onClick={handleDelete}
                  disabled={saving}
              >
                {saving ? "Deleting..." : "Delete"}
              </Button>
              <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500 font-semibold"
                  onClick={handleRegisterStaff}
                  disabled={saving || !formData.name.trim()}
              >
                  {saving ? "Saving..." : "Register Staff"}
              </Button>
            </div>
            </div>
        )}
      </div>
    </div>
  );
}